import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import TradingViewWidget, { 
  TradingViewTickerTape, 
  TradingViewTechnicalAnalysis,
  AVAILABLE_INDICATORS, 
  INDICATOR_PRESETS 
} from '@/components/TradingViewWidget';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import { 
  TrendingUp, Bitcoin, RefreshCw, Link2, Globe, Building, Home, 
  Wifi, ArrowUpRight, ArrowDownRight, Bell, BarChart3, Menu, X, Plus,
  AlertTriangle, Check, Sliders, DollarSign, Zap, Shield, Target,
  ExternalLink, Trash2, Play, Square, Newspaper, Clock
} from 'lucide-react';

import { API } from '@/config/api';

// Market categories
const MARKET_CATEGORIES = [
  { key: 'crypto', name: 'Crypto', icon: Bitcoin, tradeable: false, broker: 'Coming Soon' },
  { key: 'stocks', name: 'Stocks', icon: TrendingUp, tradeable: true, broker: 'Alpaca' },
  { key: 'forex', name: 'Forex', icon: Globe, tradeable: false, broker: 'Coming Soon' },
  { key: 'commodities', name: 'Commodities', icon: Building, tradeable: false, broker: 'Coming Soon' },
  { key: 'realestate', name: 'Real Estate', icon: Home, tradeable: false, broker: 'Coming Soon' },
];

// Advanced order types
const ORDER_TYPES = [
  { value: 'market', label: 'Market', description: 'Execute immediately at best price' },
  { value: 'limit', label: 'Limit', description: 'Execute at your specified price' },
  { value: 'stop', label: 'Stop', description: 'Trigger when price hits stop' },
  { value: 'stop_limit', label: 'Stop Limit', description: 'Stop + limit combination' },
  { value: 'trailing_stop', label: 'Trailing Stop', description: 'Dynamic trailing stop' },
];

// Stock data
const POPULAR_STOCKS = [
  { symbol: 'AAPL', name: 'Apple Inc', price: 178.50 },
  { symbol: 'MSFT', name: 'Microsoft', price: 378.90 },
  { symbol: 'GOOGL', name: 'Alphabet', price: 141.20 },
  { symbol: 'TSLA', name: 'Tesla', price: 248.30 },
  { symbol: 'NVDA', name: 'NVIDIA', price: 495.20 },
  { symbol: 'AMZN', name: 'Amazon', price: 178.50 },
  { symbol: 'META', name: 'Meta', price: 505.00 },
  { symbol: 'AMD', name: 'AMD', price: 165.80 },
  { symbol: 'NFLX', name: 'Netflix', price: 628.50 },
  { symbol: 'SPY', name: 'S&P 500 ETF', price: 502.30 },
];

// Crypto data for display
const POPULAR_CRYPTO = [
  { symbol: 'BTC', name: 'Bitcoin', id: 'bitcoin' },
  { symbol: 'ETH', name: 'Ethereum', id: 'ethereum' },
  { symbol: 'SOL', name: 'Solana', id: 'solana' },
  { symbol: 'BNB', name: 'Binance Coin', id: 'binancecoin' },
  { symbol: 'XRP', name: 'Ripple', id: 'ripple' },
  { symbol: 'ADA', name: 'Cardano', id: 'cardano' },
  { symbol: 'DOGE', name: 'Dogecoin', id: 'dogecoin' },
  { symbol: 'DOT', name: 'Polkadot', id: 'polkadot' },
];

// Timeframe mapping
const INTERVALS = { '1m': '1', '5m': '5', '15m': '15', '1h': '60', '4h': '240', '1d': 'D', '1w': 'W' };

// Get TradingView symbol format
const getTradingViewSymbol = (symbol, category) => {
  if (category === 'crypto') return `BINANCE:${symbol}USDT`;
  if (category === 'stocks') return `NASDAQ:${symbol}`;
  return `NASDAQ:${symbol}`;
};

export default function TradingTerminal() {
  const { user } = useAuth();
  
  // Core state
  const [marketCategory, setMarketCategory] = useState('stocks');
  const [selectedSymbol, setSelectedSymbol] = useState('AAPL');
  const [tradingViewSymbol, setTradingViewSymbol] = useState('NASDAQ:AAPL');
  const [currentPrice, setCurrentPrice] = useState(178.50);
  const [priceChange, setPriceChange] = useState(2.35);
  const [loading, setLoading] = useState(false);
  
  // Chart state
  const [timeframe, setTimeframe] = useState('1h');
  const [selectedIndicators, setSelectedIndicators] = useState(['Volume@tv-basicstudies']);
  const [indicatorModalOpen, setIndicatorModalOpen] = useState(false);
  const [activePreset, setActivePreset] = useState('default');
  const [showTechAnalysis, setShowTechAnalysis] = useState(false);
  
  // Trading state
  const [orderSide, setOrderSide] = useState('buy');
  const [orderType, setOrderType] = useState('market');
  const [quantity, setQuantity] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [trailPercent, setTrailPercent] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);
  
  // Alpaca state
  const [alpacaConnected, setAlpacaConnected] = useState(false);
  const [alpacaPaper, setAlpacaPaper] = useState(true);
  const [alpacaAccount, setAlpacaAccount] = useState(null);
  const [alpacaPositions, setAlpacaPositions] = useState([]);
  const [alpacaOrders, setAlpacaOrders] = useState([]);
  const [brokerModalOpen, setBrokerModalOpen] = useState(false);
  const [credentials, setCredentials] = useState({ api_key: '', secret_key: '', paper: true });
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [newAlert, setNewAlert] = useState({ target_price: '', condition: 'above' });
  
  // News feed state
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [showNews, setShowNews] = useState(true);

  // Fetch news on mount
  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    setNewsLoading(true);
    try {
      const res = await fetch(`${API}/news/financial?limit=5`);
      if (res.ok) {
        const data = await res.json();
        setNews(data.news || []);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setNewsLoading(false);
    }
  };

  // Fetch Alpaca status on mount
  useEffect(() => {
    fetchAlpacaStatus();
  }, []);

  // Update price when symbol changes
  useEffect(() => {
    const stock = POPULAR_STOCKS.find(s => s.symbol === selectedSymbol);
    if (stock) {
      setCurrentPrice(stock.price);
      setPriceChange((Math.random() * 6 - 3).toFixed(2));
    }
  }, [selectedSymbol]);

  const fetchAlpacaStatus = async () => {
    try {
      const res = await fetch(`${API}/alpaca/status`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlpacaConnected(data.connected);
        setAlpacaPaper(data.paper !== false);
        if (data.connected) {
          fetchAlpacaAccount();
          fetchAlpacaPositions();
          fetchAlpacaOrders();
        }
      }
    } catch (e) { console.error('Alpaca status error:', e); }
  };

  const fetchAlpacaAccount = async () => {
    try {
      const res = await fetch(`${API}/alpaca/account`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlpacaAccount(data);
      }
    } catch (e) { console.error('Account fetch error:', e); }
  };

  const fetchAlpacaPositions = async () => {
    try {
      const res = await fetch(`${API}/alpaca/positions`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlpacaPositions(Array.isArray(data) ? data : []);
      }
    } catch (e) { console.error('Positions fetch error:', e); }
  };

  const fetchAlpacaOrders = async () => {
    try {
      const res = await fetch(`${API}/alpaca/orders?status=all`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAlpacaOrders(Array.isArray(data) ? data.slice(0, 20) : []);
      }
    } catch (e) { console.error('Orders fetch error:', e); }
  };

  const connectAlpaca = async () => {
    if (!credentials.api_key || !credentials.secret_key) {
      toast.error('Please enter your API credentials');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/alpaca/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(credentials)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(
          <div>
            <strong>🎉 Alpaca Connected!</strong>
            <p className="text-sm mt-1">Mode: {credentials.paper ? 'Paper Trading' : 'LIVE Trading'}</p>
          </div>
        );
        setBrokerModalOpen(false);
        setAlpacaConnected(true);
        setAlpacaPaper(credentials.paper);
        fetchAlpacaAccount();
        fetchAlpacaPositions();
        fetchAlpacaOrders();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to connect');
      }
    } catch (e) {
      toast.error('Connection failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const disconnectAlpaca = async () => {
    try {
      await fetch(`${API}/alpaca/disconnect`, { method: 'POST', credentials: 'include' });
      setAlpacaConnected(false);
      setAlpacaAccount(null);
      setAlpacaPositions([]);
      setAlpacaOrders([]);
      toast.success('Disconnected from Alpaca');
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSymbolSelect = (symbol, category) => {
    setSelectedSymbol(symbol);
    setMarketCategory(category);
    setTradingViewSymbol(getTradingViewSymbol(symbol, category));
    setSidebarOpen(false);
  };

  const placeOrder = async () => {
    // Validation
    if (!quantity || parseFloat(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    if (!alpacaConnected) {
      toast.error('Please connect your Alpaca account first');
      setBrokerModalOpen(true);
      return;
    }

    if (marketCategory !== 'stocks') {
      toast.error(`${marketCategory.charAt(0).toUpperCase() + marketCategory.slice(1)} trading coming soon! Currently only stocks are available.`);
      return;
    }

    // Validate order type requirements
    if (orderType === 'limit' && !limitPrice) {
      toast.error('Limit price required for limit orders');
      return;
    }
    if (orderType === 'stop' && !stopPrice) {
      toast.error('Stop price required for stop orders');
      return;
    }
    if (orderType === 'stop_limit' && (!stopPrice || !limitPrice)) {
      toast.error('Stop and limit prices required');
      return;
    }
    if (orderType === 'trailing_stop' && !trailPercent) {
      toast.error('Trail percentage required');
      return;
    }

    // Live trading warning
    if (!alpacaPaper) {
      const confirmed = window.confirm(
        `⚠️ LIVE TRADING WARNING ⚠️\n\n` +
        `You are about to place a REAL order:\n\n` +
        `${orderSide.toUpperCase()} ${quantity} shares of ${selectedSymbol}\n` +
        `Order Type: ${orderType.toUpperCase()}\n` +
        `Estimated Value: $${(parseFloat(quantity) * currentPrice).toFixed(2)}\n\n` +
        `This will use REAL MONEY. Are you sure?`
      );
      if (!confirmed) return;
    }

    setOrderLoading(true);
    try {
      const orderData = {
        symbol: selectedSymbol,
        qty: parseFloat(quantity),
        side: orderSide,
        order_type: orderType,
        time_in_force: 'day'
      };

      if (orderType === 'limit') orderData.limit_price = parseFloat(limitPrice);
      if (orderType === 'stop') orderData.stop_price = parseFloat(stopPrice);
      if (orderType === 'stop_limit') {
        orderData.stop_price = parseFloat(stopPrice);
        orderData.limit_price = parseFloat(limitPrice);
      }
      if (orderType === 'trailing_stop') orderData.trail_percent = parseFloat(trailPercent);

      const res = await fetch(`${API}/alpaca/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(orderData)
      });

      if (res.ok) {
        const result = await res.json();
        const modeEmoji = alpacaPaper ? '📝' : '💰';
        const modeText = alpacaPaper ? 'PAPER' : 'LIVE';
        
        toast.success(
          <div>
            <strong>{modeEmoji} {modeText} Order Placed!</strong>
            <p className="text-sm mt-1">
              {result.side?.toUpperCase()} {result.qty} {result.symbol}
            </p>
            <p className="text-xs text-gray-400">
              Order ID: {result.order_id?.slice(0, 8)}... | Status: {result.status}
            </p>
          </div>,
          { duration: 5000 }
        );

        // Reset form and refresh data
        setQuantity('');
        setLimitPrice('');
        setStopPrice('');
        setTrailPercent('');
        fetchAlpacaPositions();
        fetchAlpacaOrders();
        fetchAlpacaAccount();
      } else {
        const error = await res.json();
        toast.error(
          <div>
            <strong>Order Failed</strong>
            <p className="text-sm mt-1">{error.detail || 'Unknown error'}</p>
          </div>
        );
      }
    } catch (e) {
      toast.error(`Order failed: ${e.message}`);
    } finally {
      setOrderLoading(false);
    }
  };

  const cancelOrder = async (orderId) => {
    try {
      const res = await fetch(`${API}/alpaca/orders/${orderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Order cancelled');
        fetchAlpacaOrders();
      } else {
        toast.error('Failed to cancel order');
      }
    } catch (e) {
      toast.error('Failed to cancel order');
    }
  };

  const closePosition = async (symbol) => {
    if (!window.confirm(`Close entire position in ${symbol}?`)) return;
    
    try {
      const res = await fetch(`${API}/alpaca/positions/${symbol}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success(`Position in ${symbol} closed`);
        fetchAlpacaPositions();
        fetchAlpacaAccount();
      } else {
        toast.error('Failed to close position');
      }
    } catch (e) {
      toast.error('Failed to close position');
    }
  };

  const applyPreset = (preset) => {
    setSelectedIndicators(INDICATOR_PRESETS[preset] || []);
    setActivePreset(preset);
  };

  const toggleIndicator = (id) => {
    setSelectedIndicators(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
    setActivePreset('custom');
  };

  const formatCurrency = (v) => {
    if (!v && v !== 0) return '$0.00';
    return `$${parseFloat(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const orderValue = (parseFloat(quantity) || 0) * currentPrice;
  const buyingPower = alpacaAccount?.buying_power || 0;
  const isTradeable = MARKET_CATEGORIES.find(m => m.key === marketCategory)?.tradeable;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white pt-16" data-testid="trading-terminal">
      <SEO {...SEO_CONFIG.trading} />
      
      {/* Ticker Tape */}
      <div className="border-b border-[#1c1814] bg-[#0d0d12]">
        <TradingViewTickerTape theme="dark" />
      </div>

      {/* Top Control Bar */}
      <div className="border-b border-[#1c1814] bg-[#0d0d12] sticky top-16 z-40">
        <div className="max-w-[1800px] mx-auto px-3 py-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {/* Left: Symbol Selection */}
            <div className="flex items-center gap-3">
              {/* Mobile Menu */}
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="sm" className="p-2">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-[#0d0d12] border-[#1c1814] w-[300px]">
                  <SheetHeader>
                    <SheetTitle className="text-white">Select Asset</SheetTitle>
                  </SheetHeader>
                  <div className="py-4 space-y-4">
                    <div className="flex gap-2 flex-wrap">
                      {MARKET_CATEGORIES.map(({ key, name, icon: Icon }) => (
                        <button
                          key={key}
                          onClick={() => setMarketCategory(key)}
                          className={`px-3 py-1.5 rounded-full text-xs flex items-center gap-1 ${marketCategory === key ? 'bg-[#D4AF37] text-black' : 'bg-[#1c1814] text-gray-400'}`}
                        >
                          <Icon className="h-3 w-3" /> {name}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-1 max-h-[400px] overflow-auto">
                      {marketCategory === 'stocks' && POPULAR_STOCKS.map(stock => (
                        <button
                          key={stock.symbol}
                          onClick={() => handleSymbolSelect(stock.symbol, 'stocks')}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm ${selectedSymbol === stock.symbol ? 'bg-[#D4AF37]/20 text-white' : 'text-gray-400 hover:bg-[#1c1814]'}`}
                        >
                          <span className="font-medium">{stock.symbol}</span>
                          <span className="text-xs text-gray-500">{stock.name}</span>
                        </button>
                      ))}
                      {marketCategory === 'crypto' && POPULAR_CRYPTO.map(crypto => (
                        <button
                          key={crypto.symbol}
                          onClick={() => handleSymbolSelect(crypto.symbol, 'crypto')}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm ${selectedSymbol === crypto.symbol ? 'bg-[#D4AF37]/20 text-white' : 'text-gray-400 hover:bg-[#1c1814]'}`}
                        >
                          <span className="font-medium">{crypto.symbol}</span>
                          <span className="text-xs text-gray-500">{crypto.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </SheetContent>
              </Sheet>

              {/* Market & Symbol Selectors */}
              <div className="hidden lg:flex items-center gap-2">
                <Select value={marketCategory} onValueChange={setMarketCategory}>
                  <SelectTrigger className="w-28 bg-[#14120f] border-[#1c1814] text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814]">
                    {MARKET_CATEGORIES.map(({ key, name, icon: Icon, tradeable }) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <span className="flex items-center gap-2">
                          <Icon className="h-4 w-4" /> {name}
                          {!tradeable && <span className="text-[10px] text-gray-500">(View)</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={selectedSymbol} onValueChange={(v) => handleSymbolSelect(v, marketCategory)}>
                  <SelectTrigger className="w-36 bg-[#14120f] border-[#1c1814] text-white h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14120f] border-[#1c1814] max-h-80">
                    {marketCategory === 'stocks' && POPULAR_STOCKS.map(s => (
                      <SelectItem key={s.symbol} value={s.symbol} className="text-white">
                        {s.symbol} - {s.name}
                      </SelectItem>
                    ))}
                    {marketCategory === 'crypto' && POPULAR_CRYPTO.map(c => (
                      <SelectItem key={c.symbol} value={c.symbol} className="text-white">
                        {c.symbol} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Current Price */}
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-[#D4AF37]">{formatCurrency(currentPrice)}</span>
                <Badge className={`${priceChange >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                  {priceChange >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                  {priceChange >= 0 ? '+' : ''}{priceChange}%
                </Badge>
              </div>
            </div>

            {/* Right: Account & Actions */}
            <div className="flex items-center gap-2">
              {/* Quick Action Buttons */}
              <Button variant="ghost" size="sm" onClick={() => setIndicatorModalOpen(true)} className="hidden sm:flex text-gray-400 hover:text-[#D4AF37]">
                <Sliders className="h-4 w-4 mr-1" /> Indicators
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setShowTechAnalysis(!showTechAnalysis)} className="hidden sm:flex text-gray-400 hover:text-[#D4AF37]">
                <BarChart3 className="h-4 w-4 mr-1" /> Analysis
              </Button>

              {/* Account Balance */}
              {alpacaConnected && alpacaAccount && (
                <div className="hidden md:block text-right px-3 border-l border-[#1c1814]">
                  <p className="text-xs text-gray-500">Buying Power</p>
                  <p className="font-bold text-[#D4AF37]">{formatCurrency(buyingPower)}</p>
                </div>
              )}

              {/* Connect/Status Button */}
              <Button
                onClick={() => setBrokerModalOpen(true)}
                className={`${alpacaConnected 
                  ? (alpacaPaper ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/50')
                  : 'bg-[#D4AF37] text-black hover:bg-[#B8962E]'
                }`}
                data-testid="broker-connect-btn"
              >
                <Link2 className="h-4 w-4 mr-2" />
                {alpacaConnected ? (alpacaPaper ? '📝 Paper' : '💰 Live') : 'Connect Alpaca'}
              </Button>
            </div>
          </div>

          {/* Timeframes */}
          <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-1">
            {Object.keys(INTERVALS).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-3 py-1 text-xs rounded ${timeframe === tf ? 'bg-[#D4AF37] text-black font-medium' : 'bg-[#14120f] text-gray-500 hover:text-white'}`}
              >
                {tf}
              </button>
            ))}
            <div className="w-px h-5 bg-[#1c1814] mx-1" />
            {Object.keys(INDICATOR_PRESETS).slice(0, 4).map(preset => (
              <button
                key={preset}
                onClick={() => applyPreset(preset)}
                className={`px-2 py-1 text-xs rounded capitalize ${activePreset === preset ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#14120f] text-gray-500'}`}
              >
                {preset}
              </button>
            ))}
            <Button variant="ghost" size="sm" onClick={fetchAlpacaStatus} className="h-7 w-7 p-0">
              <RefreshCw className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-3 py-3">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
          {/* Chart Section - 3 columns */}
          <div className="xl:col-span-3 space-y-4">
            {/* TradingView Chart */}
            <div className="bg-[#0d0d12] border border-[#1c1814] rounded-lg overflow-hidden">
              <TradingViewWidget
                symbol={tradingViewSymbol}
                theme="dark"
                height={500}
                indicators={selectedIndicators}
                interval={INTERVALS[timeframe]}
              />
            </div>

            {/* Technical Analysis (Collapsible) */}
            {showTechAnalysis && (
              <div className="bg-[#0d0d12] border border-[#1c1814] rounded-lg overflow-hidden">
                <div className="px-3 py-2 border-b border-[#1c1814] flex items-center justify-between">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-[#D4AF37]" /> Technical Analysis
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setShowTechAnalysis(false)} className="h-6 w-6 p-0">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <TradingViewTechnicalAnalysis symbol={tradingViewSymbol} theme="dark" height={280} />
              </div>
            )}

            {/* Positions & Orders */}
            {alpacaConnected && (
              <div className="grid md:grid-cols-2 gap-4">
                {/* Positions */}
                <Card className="bg-[#0d0d12] border-[#1c1814]">
                  <CardHeader className="py-3 px-4 border-b border-[#1c1814]">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-[#D4AF37]" /> Positions ({alpacaPositions.length})
                      </span>
                      <Badge className={alpacaPaper ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}>
                        {alpacaPaper ? 'Paper' : 'Live'}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {alpacaPositions.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-6">No open positions</p>
                    ) : (
                      <div className="max-h-[200px] overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#0a0a0f] sticky top-0">
                            <tr className="text-gray-500">
                              <th className="px-3 py-2 text-left">Symbol</th>
                              <th className="px-3 py-2 text-right">Qty</th>
                              <th className="px-3 py-2 text-right">P&L</th>
                              <th className="px-3 py-2 text-right"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {alpacaPositions.map((pos, i) => (
                              <tr key={i} className="border-t border-[#1c1814]">
                                <td className="px-3 py-2 font-medium text-white">{pos.symbol}</td>
                                <td className="px-3 py-2 text-right text-gray-300">{pos.qty}</td>
                                <td className={`px-3 py-2 text-right font-medium ${pos.unrealized_pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {pos.unrealized_pl >= 0 ? '+' : ''}{formatCurrency(pos.unrealized_pl)}
                                </td>
                                <td className="px-3 py-2 text-right">
                                  <Button variant="ghost" size="sm" onClick={() => closePosition(pos.symbol)} className="h-6 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10">
                                    Close
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Orders */}
                <Card className="bg-[#0d0d12] border-[#1c1814]">
                  <CardHeader className="py-3 px-4 border-b border-[#1c1814]">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <BarChart3 className="h-4 w-4 text-[#D4AF37]" /> Recent Orders
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {alpacaOrders.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-6">No recent orders</p>
                    ) : (
                      <div className="max-h-[200px] overflow-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-[#0a0a0f] sticky top-0">
                            <tr className="text-gray-500">
                              <th className="px-3 py-2 text-left">Symbol</th>
                              <th className="px-3 py-2 text-left">Side</th>
                              <th className="px-3 py-2 text-right">Qty</th>
                              <th className="px-3 py-2 text-center">Status</th>
                              <th className="px-3 py-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {alpacaOrders.slice(0, 8).map((order, i) => (
                              <tr key={i} className="border-t border-[#1c1814]">
                                <td className="px-3 py-2 font-medium text-white">{order.symbol}</td>
                                <td className={`px-3 py-2 font-medium ${order.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {order.side?.toUpperCase()}
                                </td>
                                <td className="px-3 py-2 text-right text-gray-300">{order.qty}</td>
                                <td className="px-3 py-2 text-center">
                                  <Badge className={`text-[10px] ${
                                    order.status === 'filled' ? 'bg-emerald-500/20 text-emerald-400' :
                                    order.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                                    order.status === 'canceled' ? 'bg-gray-500/20 text-gray-400' :
                                    'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {order.status}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2">
                                  {(order.status === 'new' || order.status === 'pending_new') && (
                                    <Button variant="ghost" size="sm" onClick={() => cancelOrder(order.id)} className="h-5 w-5 p-0 text-red-400">
                                      <X className="h-3 w-3" />
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>

          {/* Trading Panel - 1 column */}
          <div className="space-y-4">
            {/* Order Form */}
            <Card className="bg-[#0d0d12] border-[#1c1814]">
              <CardHeader className="py-3 px-4 border-b border-[#1c1814]">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-[#D4AF37]" /> Place Order
                  </span>
                  {alpacaConnected && (
                    <Badge className={alpacaPaper ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}>
                      {alpacaPaper ? '📝 Paper' : '💰 Live'}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* Not Connected Warning */}
                {!alpacaConnected && (
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-center">
                    <AlertTriangle className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                    <p className="text-sm text-yellow-400 font-medium">Connect Alpaca to Trade</p>
                    <p className="text-xs text-gray-400 mt-1">Get your API keys from alpaca.markets</p>
                    <Button onClick={() => setBrokerModalOpen(true)} className="mt-3 bg-[#D4AF37] text-black w-full">
                      <Link2 className="h-4 w-4 mr-2" /> Connect Now
                    </Button>
                  </div>
                )}

                {/* Live Trading Warning */}
                {alpacaConnected && !alpacaPaper && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-center">
                    <p className="text-xs text-red-400 font-bold flex items-center justify-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> LIVE TRADING - REAL MONEY
                    </p>
                  </div>
                )}

                {/* Not Tradeable Warning */}
                {!isTradeable && (
                  <div className="bg-gray-500/10 border border-gray-500/30 rounded-lg p-2 text-center">
                    <p className="text-xs text-gray-400">
                      {marketCategory.charAt(0).toUpperCase() + marketCategory.slice(1)} trading coming soon.
                      <br />Only <span className="text-[#D4AF37]">Stocks</span> are currently tradeable via Alpaca.
                    </p>
                  </div>
                )}

                {/* Order Type */}
                <div>
                  <Label className="text-xs text-gray-500">Order Type</Label>
                  <Select value={orderType} onValueChange={setOrderType}>
                    <SelectTrigger className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#14120f] border-[#1c1814]">
                      {ORDER_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value} className="text-white">
                          <div className="flex flex-col">
                            <span>{type.label}</span>
                            <span className="text-[10px] text-gray-500">{type.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Buy/Sell Toggle */}
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    onClick={() => setOrderSide('buy')}
                    className={`py-5 font-bold ${orderSide === 'buy' ? 'bg-emerald-500 text-white' : 'bg-[#0a0a0f] text-emerald-400 border border-emerald-500/30'}`}
                    data-testid="buy-btn"
                  >
                    <ArrowUpRight className="h-4 w-4 mr-1" /> BUY
                  </Button>
                  <Button
                    onClick={() => setOrderSide('sell')}
                    className={`py-5 font-bold ${orderSide === 'sell' ? 'bg-red-500 text-white' : 'bg-[#0a0a0f] text-red-400 border border-red-500/30'}`}
                    data-testid="sell-btn"
                  >
                    <ArrowDownRight className="h-4 w-4 mr-1" /> SELL
                  </Button>
                </div>

                {/* Quantity */}
                <div>
                  <div className="flex justify-between">
                    <Label className="text-xs text-gray-500">Quantity (shares)</Label>
                    {alpacaConnected && <span className="text-[10px] text-gray-600">{formatCurrency(buyingPower)} available</span>}
                  </div>
                  <Input
                    type="number"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="0"
                    className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white text-lg h-12"
                    data-testid="quantity-input"
                  />
                  <div className="flex gap-1 mt-2">
                    {[1, 5, 10, 25, 50, 100].map(n => (
                      <button
                        key={n}
                        onClick={() => setQuantity(n.toString())}
                        className="flex-1 py-1 text-[10px] bg-[#0a0a0f] border border-[#1c1814] rounded text-gray-500 hover:text-white hover:border-[#D4AF37]"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Conditional Fields */}
                {orderType === 'limit' && (
                  <div>
                    <Label className="text-xs text-gray-500">Limit Price</Label>
                    <Input
                      type="number"
                      value={limitPrice}
                      onChange={(e) => setLimitPrice(e.target.value)}
                      placeholder={currentPrice.toFixed(2)}
                      className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                    />
                  </div>
                )}

                {(orderType === 'stop' || orderType === 'stop_limit') && (
                  <div>
                    <Label className="text-xs text-gray-500">Stop Price</Label>
                    <Input
                      type="number"
                      value={stopPrice}
                      onChange={(e) => setStopPrice(e.target.value)}
                      placeholder="Trigger price"
                      className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                    />
                  </div>
                )}

                {orderType === 'stop_limit' && (
                  <div>
                    <Label className="text-xs text-gray-500">Limit Price</Label>
                    <Input
                      type="number"
                      value={limitPrice}
                      onChange={(e) => setLimitPrice(e.target.value)}
                      placeholder="Execute at"
                      className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                    />
                  </div>
                )}

                {orderType === 'trailing_stop' && (
                  <div>
                    <Label className="text-xs text-gray-500">Trail %</Label>
                    <Input
                      type="number"
                      value={trailPercent}
                      onChange={(e) => setTrailPercent(e.target.value)}
                      placeholder="e.g., 2.5"
                      className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                    />
                  </div>
                )}

                {/* Order Summary */}
                <div className="bg-[#0a0a0f] rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Symbol</span>
                    <span className="text-white font-medium">{selectedSymbol}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Price</span>
                    <span className="text-white">{formatCurrency(currentPrice)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Order Value</span>
                    <span className="text-[#D4AF37] font-bold">{formatCurrency(orderValue)}</span>
                  </div>
                </div>

                {/* Place Order Button */}
                <Button
                  onClick={placeOrder}
                  disabled={orderLoading || !quantity || (!alpacaConnected && isTradeable)}
                  className={`w-full py-6 font-bold text-lg ${orderSide === 'buy' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'}`}
                  data-testid="place-order-btn"
                >
                  {orderLoading ? (
                    'Processing...'
                  ) : (
                    <>
                      {alpacaConnected && !alpacaPaper && '💰 '}
                      {alpacaConnected && alpacaPaper && '📝 '}
                      {orderSide === 'buy' ? 'Buy' : 'Sell'} {selectedSymbol}
                    </>
                  )}
                </Button>

                {alpacaConnected && alpacaPaper && (
                  <p className="text-[10px] text-center text-gray-500">Paper trading uses virtual funds - no real money at risk</p>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            {alpacaConnected && alpacaAccount && (
              <Card className="bg-[#0d0d12] border-[#1c1814]">
                <CardHeader className="py-3 px-4 border-b border-[#1c1814]">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-[#D4AF37]" /> Account
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">Portfolio Value</span>
                    <span className="text-white font-bold">{formatCurrency(alpacaAccount.portfolio_value)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">Cash</span>
                    <span className="text-gray-300">{formatCurrency(alpacaAccount.cash)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">Buying Power</span>
                    <span className="text-emerald-400">{formatCurrency(alpacaAccount.buying_power)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 text-sm">Open Positions</span>
                    <span className="text-white">{alpacaPositions.length}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Market News Feed */}
            {showNews && (
              <Card className="bg-[#0d0d12] border-[#1c1814]">
                <CardHeader className="py-3 px-4 border-b border-[#1c1814]">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Newspaper className="h-4 w-4 text-[#D4AF37]" />
                      Market News
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={fetchNews}
                      className="h-6 w-6 p-0 text-gray-400 hover:text-white"
                    >
                      <RefreshCw className={`h-3 w-3 ${newsLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-2 max-h-[400px] overflow-y-auto">
                  {newsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <RefreshCw className="h-5 w-5 animate-spin text-[#D4AF37]" />
                    </div>
                  ) : news.length === 0 ? (
                    <p className="text-gray-500 text-sm text-center py-4">No news available</p>
                  ) : (
                    <div className="space-y-2">
                      {news.map((item) => (
                        <a
                          key={item.id}
                          href={item.internal ? item.url : item.url}
                          target={item.internal ? "_self" : "_blank"}
                          rel="noopener noreferrer"
                          className="block p-2 rounded-lg hover:bg-[#1c1814] transition-colors group"
                        >
                          <div className="flex gap-2">
                            {item.image_url && (
                              <img 
                                src={item.image_url} 
                                alt="" 
                                className="w-12 h-12 rounded object-cover flex-shrink-0"
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <h4 className="text-xs font-medium text-white group-hover:text-[#D4AF37] line-clamp-2 transition-colors">
                                {item.title}
                              </h4>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-gray-500">{item.source}</span>
                                <span className={`text-[10px] px-1 rounded ${
                                  item.sentiment === 'positive' ? 'bg-emerald-500/20 text-emerald-400' :
                                  item.sentiment === 'negative' ? 'bg-red-500/20 text-red-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {item.sentiment}
                                </span>
                              </div>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Broker Connection Modal */}
      <Dialog open={brokerModalOpen} onOpenChange={setBrokerModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 className="h-5 w-5 text-[#D4AF37]" />
              {alpacaConnected ? 'Alpaca Account' : 'Connect Alpaca'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {alpacaConnected ? 'Manage your broker connection' : 'Connect your Alpaca account to start trading stocks'}
            </DialogDescription>
          </DialogHeader>

          {alpacaConnected ? (
            <div className="space-y-4 py-3">
              <div className="bg-[#0a0a0f] rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status</span>
                  <Badge className={alpacaPaper ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}>
                    Connected ({alpacaPaper ? 'Paper' : 'Live'})
                  </Badge>
                </div>
                {alpacaAccount && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Portfolio Value</span>
                      <span className="text-white font-bold">{formatCurrency(alpacaAccount.portfolio_value)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Buying Power</span>
                      <span className="text-emerald-400">{formatCurrency(alpacaAccount.buying_power)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Positions</span>
                      <span className="text-white">{alpacaPositions.length}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={fetchAlpacaStatus} className="flex-1 border-gray-700 text-gray-400">
                  <RefreshCw className="h-4 w-4 mr-1" /> Refresh
                </Button>
                <Button variant="destructive" onClick={disconnectAlpaca} className="flex-1">
                  Disconnect
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-3">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                <p className="text-sm text-blue-400">
                  Get your API keys from{' '}
                  <a href="https://app.alpaca.markets/brokerage/api-access" target="_blank" rel="noopener noreferrer" className="underline">
                    Alpaca Dashboard <ExternalLink className="h-3 w-3 inline" />
                  </a>
                </p>
              </div>

              <div>
                <Label className="text-gray-400 text-xs">API Key</Label>
                <Input
                  value={credentials.api_key}
                  onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                  placeholder="PK..."
                  className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>

              <div>
                <Label className="text-gray-400 text-xs">Secret Key</Label>
                <Input
                  type="password"
                  value={credentials.secret_key}
                  onChange={(e) => setCredentials({ ...credentials, secret_key: e.target.value })}
                  placeholder="••••••••"
                  className="mt-1 bg-[#0a0a0f] border-[#1c1814] text-white"
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-[#0a0a0f] rounded-lg">
                <div>
                  <p className="text-white text-sm">Paper Trading</p>
                  <p className="text-gray-500 text-xs">Practice with virtual money</p>
                </div>
                <Switch
                  checked={credentials.paper}
                  onCheckedChange={(c) => setCredentials({ ...credentials, paper: c })}
                />
              </div>

              {!credentials.paper && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    <strong>Warning:</strong> Live trading uses real money!
                  </p>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setBrokerModalOpen(false)} className="border-gray-700 text-gray-400">
                  Cancel
                </Button>
                <Button onClick={connectAlpaca} disabled={loading} className="bg-[#D4AF37] text-black">
                  {loading ? 'Connecting...' : 'Connect'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Indicator Modal */}
      <Dialog open={indicatorModalOpen} onOpenChange={setIndicatorModalOpen}>
        <DialogContent className="bg-[#14120f] border-[#1c1814] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sliders className="h-5 w-5 text-[#D4AF37]" /> Chart Indicators
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="all" className="mt-2">
            <TabsList className="bg-[#0a0a0f]">
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="momentum">Momentum</TabsTrigger>
              <TabsTrigger value="trend">Trend</TabsTrigger>
              <TabsTrigger value="volume">Volume</TabsTrigger>
            </TabsList>
            
            {['all', 'momentum', 'trend', 'volume'].map(category => (
              <TabsContent key={category} value={category} className="mt-4">
                <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-auto">
                  {AVAILABLE_INDICATORS
                    .filter(ind => category === 'all' || ind.category === category)
                    .map(indicator => (
                      <button
                        key={indicator.id}
                        onClick={() => toggleIndicator(indicator.id)}
                        className={`flex items-center justify-between p-2 rounded border transition-colors ${
                          selectedIndicators.includes(indicator.id)
                            ? 'bg-[#D4AF37]/20 border-[#D4AF37]/50 text-white'
                            : 'bg-[#0a0a0f] border-[#1c1814] text-gray-400'
                        }`}
                      >
                        <span className="text-sm">{indicator.name}</span>
                        {selectedIndicators.includes(indicator.id) && <Check className="h-4 w-4 text-[#D4AF37]" />}
                      </button>
                    ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>

          <div className="mt-4 pt-4 border-t border-[#1c1814]">
            <p className="text-xs text-gray-500 mb-2">Quick Presets</p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(INDICATOR_PRESETS).map(preset => (
                <Button
                  key={preset}
                  variant="outline"
                  size="sm"
                  onClick={() => { applyPreset(preset); setIndicatorModalOpen(false); }}
                  className={`capitalize ${activePreset === preset ? 'bg-[#D4AF37]/20 border-[#D4AF37]' : 'border-[#1c1814]'}`}
                >
                  {preset}
                </Button>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setIndicatorModalOpen(false)} className="bg-[#D4AF37] text-black">
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
